// 농심DAS — 작업 한 건(/pdas/:id). 상태에 따라 세 모양:
//   준비(draft) = 셀 수 · 빈 양식 · 명단 올리기(오류 행 표) · 작업 시작
//   작업 중(active) = PDA 스캔 화면(휴대 폭 360px 한 손 기준) · 큰 «N번 셀» · 빼세요 단추 · 셀 판 · 5초 갱신
//   마감(closed) = 요약 · 결과 엑셀 · 명단 표
// 🔑 셀은 서버가 정한다(backend/routers/pdas.py). 화면은 스캔을 «한 번에 하나씩» 보낸다(순서가 섞이면 셀 지정이 틀린다)
//    · 끊기면 같은 scan_id 로 다시 보낸다(서버가 같은 scan_id 는 한 번만 넣는다).
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { pdasAPI } from '../api/client';
import { COLORS, badge, btn, card, input } from '../utils/uiStyles';
import { fmtKstDateTime } from '../utils/kstTime';
import {
  BUNDLE_STATUS_LABELS, JOB_STATUS_LABELS, REJECT_SHORT,
  downloadBlob, errorDetail, errorText, errorTextSync, isNetworkError, labelOf, resultFileName,
} from './pdas/pdasLabels';

const DUP_WINDOW_MS = 300;       // 같은 바코드가 이 안에 다시 오면 스캐너 이중 읽힘으로 본다
const RETRY_MS = 2000;           // 끊김 재시도 간격
const POLL_MS = 5000;            // 다른 PDA 가 바꾼 셀 판·진행 현황 따라가기
const UNREMOVE_MS = 5000;        // 빼냄 «취소» 단추가 남는 시간
const CONFIRM_GUARD_MS = 500;    // 확인창이 열린 직후의 키 입력은 받지 않는다
const CONFIRM_BURST_MS = 100;    // 이보다 빠른 연속 키(스캐너 입력)도 받지 않는다
const QUEUE_SHOW = 3;            // 줄이 이보다 길면 «대기 n건»
const PLAN_PREVIEW = 50;
const PLAN_PAGE = 100;
const CELLS_MAX = 500;
const SOUND_KEY = 'pdasSound';

const th = { padding: '7px 8px', textAlign: 'left', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '6px 8px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' };

// 결과 판 바탕색(글자는 흰색) — 넣기 초록 · 완성/강제 마감 주황 · 거절 빨강 · 끊김 회색 · 안내 남색.
const RESULT_BG = {
  put: '#16a34a', complete: '#ea580c', forced: '#ea580c', reject: '#dc2626', offline: '#6b7280', info: '#334155',
};
// 셀 판 색 — 빈 셀 회색 · 채우는 중 파랑 · 완성/강제 마감 주황.
const CELL_STYLE = {
  empty: { background: '#f3f4f6', borderColor: '#e5e7eb', color: COLORS.textSub },
  filling: { background: '#dbeafe', borderColor: '#60a5fa', color: '#1e3a8a' },
  complete: { background: '#ffedd5', borderColor: '#fb923c', color: '#9a3412' },
  forced: { background: '#ffedd5', borderColor: '#fb923c', color: '#9a3412' },
};

const validCells = (text) => /^\d+$/.test(String(text).trim()) && Number(text) >= 1 && Number(text) <= CELLS_MAX;

function newScanId() {
  try {
    const c = typeof window !== 'undefined' ? window.crypto : null;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  } catch { /* 아래 대체값 */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function readSound() {
  try { return localStorage.getItem(SOUND_KEY) !== '0'; } catch { return true; }
}

// 짧은 삑 — 파일 없이 WebAudio 발진기(성공·거절 높이 다르게). 지원 안 하면 조용히.
let audioCtx = null;
function beep(kind) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!audioCtx) audioCtx = new Ctx();
    const tones = { put: [1200, 0.08], complete: [1600, 0.18], reject: [220, 0.35], warn: [500, 0.05] };
    const [freq, sec] = tones[kind] || tones.put;
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = 'square';
    osc.frequency.value = freq;
    gain.gain.value = 0.06;
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start();
    osc.stop(audioCtx.currentTime + sec);
  } catch { /* 소리 없이 */ }
}

function vibrate(pattern) {
  try { if (navigator.vibrate) navigator.vibrate(pattern); } catch { /* 진동 없음 */ }
}

const VIBRATE = { put: 60, complete: [80, 60, 80], reject: 400, warn: 30 };

const putLine = (d) => {
  const parts = [`${d.cell_no}번`];
  if (d.bundle_no) parts.push(d.bundle_no);
  if (d.completed) parts.push('완성');
  else if (d.qty !== undefined && d.plan_qty !== undefined) parts.push(`${d.qty}/${d.plan_qty}`);
  return parts.join(' · ');
};

const bundleLine = (r) => {
  const parts = [];
  if (r.bundleNo) parts.push(`묶음 ${r.bundleNo}`);
  if (r.qty !== undefined && r.planQty !== undefined) parts.push(`${r.qty}/${r.planQty}`);
  return parts.join(' · ');
};

/**
 * 확인창 — 스캐너 끝의 Enter 가 «확인»을 누르지 않게:
 *  · 초점은 «취소»에 둔다
 *  · 열린 뒤 500ms 안의 키 입력과 100ms 안에 연달아 오는 키 입력(스캐너)은 받지 않는다(keydown·keyup 둘 다 막는다).
 */
function GuardedConfirm({ message, confirmLabel, onConfirm, onCancel }) {
  const cancelRef = useRef(null);
  const openedAtRef = useRef(Date.now());
  const lastKeyRef = useRef(0);
  const blockedRef = useRef(false);

  useEffect(() => { if (cancelRef.current) cancelRef.current.focus(); }, []);

  const onKeyDownCapture = (e) => {
    const now = Date.now();
    const tooSoon = now - openedAtRef.current < CONFIRM_GUARD_MS;
    const burst = now - lastKeyRef.current < CONFIRM_BURST_MS;
    lastKeyRef.current = now;
    if (tooSoon || burst) {
      blockedRef.current = true;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    blockedRef.current = false;
    if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };
  const onKeyUpCapture = (e) => {
    if (!blockedRef.current) return;
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '12px' }}>
      <div role="dialog" aria-modal="true" aria-label="확인" data-testid="pdas-confirm"
        onKeyDownCapture={onKeyDownCapture} onKeyUpCapture={onKeyUpCapture}
        style={{ ...card, width: '100%', maxWidth: '360px' }}>
        <p style={{ margin: '0 0 16px', fontSize: '15px', lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button type="button" ref={cancelRef} onClick={onCancel} style={btn('secondary')}>취소</button>
          <button type="button" onClick={onConfirm} style={btn('primary')}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

export default function PdasJobPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  // ── 서버 상태 ──
  const [job, setJob] = useState(null);
  const [progress, setProgress] = useState(null);
  const [board, setBoard] = useState([]);
  const [readyCells, setReadyCells] = useState([]);
  const [loadError, setLoadError] = useState('');

  // ── 공통 화면 상태 ──
  const [confirmBox, setConfirmBox] = useState(null); // { message, confirmLabel, onConfirm }
  const [menuOpen, setMenuOpen] = useState(false);  // 머리 «…»
  const [notice, setNotice] = useState('');         // 마감 거절 등 일반 경고
  const [downloading, setDownloading] = useState(false);
  const [soundOn, setSoundOn] = useState(readSound);

  // ── 준비 ──
  const [cellInput, setCellInput] = useState('');
  const [savingCells, setSavingCells] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState(null);  // { rows, barcodes, units_plan, warnings }
  const [planErrors, setPlanErrors] = useState(null);  // { message, errors: [{row,message}] }
  const [draftError, setDraftError] = useState('');
  const [planPreview, setPlanPreview] = useState(null);
  const [starting, setStarting] = useState(false);

  // ── 작업 중 ──
  const [scanText, setScanText] = useState('');
  const [result, setResult] = useState(null);         // 결과 판
  const [recent, setRecent] = useState([]);           // 최근 3건
  const [lastPut, setLastPut] = useState(null);       // { putId, undone }
  const [queueCount, setQueueCount] = useState(0);
  const [removing, setRemoving] = useState(() => new Set());
  const [undoRemove, setUndoRemove] = useState(null); // { cell, bundleId }
  const [boardOpen, setBoardOpen] = useState(true);
  const [cellMenu, setCellMenu] = useState(null);     // «…» 메뉴가 열린 셀 번호
  const [undoing, setUndoing] = useState(false);
  const [closing, setClosing] = useState(false);

  // ── 마감 ──
  const [planStatus, setPlanStatus] = useState('');
  const [planPage, setPlanPage] = useState(1);
  const [planList, setPlanList] = useState(null);

  const mountedRef = useRef(true);
  const lastEventRef = useRef(null);     // 화면이 아는 가장 새 이력 번호(낡은 폴링 응답 버리기)
  const mutationSeqRef = useRef(0);      // 쓰기 응답을 받을 때마다 +1(그 전에 나간 폴링 응답은 버린다)
  const pollingRef = useRef(false);
  const scanRef = useRef(null);
  const fileRef = useRef(null);
  const queueRef = useRef([]);           // [{ barcode, scanId }] — 맨 앞이 보내는 중(또는 재시도 대기)
  const sendingRef = useRef(false);
  const retryTimerRef = useRef(null);
  const lastScanRef = useRef(null);      // 직전에 받아들인 스캔 { barcode, at }
  const recentSeqRef = useRef(0);
  const removingRef = useRef(new Set());
  const unremoveTimerRef = useRef(null);
  const unremovingRef = useRef(false);
  const undoingRef = useRef(false);
  const closingRef = useRef(false);
  const downloadingRef = useRef(false);
  const soundRef = useRef(soundOn);
  const overlayOpenRef = useRef(false);
  const planSeqRef = useRef(0);

  soundRef.current = soundOn;
  // 겹쳐 뜬 것(확인창·«…» 메뉴·셀 메뉴)이 열려 있으면 스캔 칸으로 초점을 되돌리지 않는다.
  overlayOpenRef.current = !!confirmBox || menuOpen || cellMenu !== null;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(retryTimerRef.current);
      clearTimeout(unremoveTimerRef.current);
    };
  }, []);

  // «…» 메뉴 밖을 누르면 닫는다 — 열린 채로 두면 스캔 칸으로 초점이 돌아가지 않는다.
  useEffect(() => {
    if (!menuOpen && cellMenu === null) return undefined;
    const onDown = (e) => {
      if (e.target && typeof e.target.closest === 'function' && e.target.closest('[data-pdas-menu]')) return;
      setMenuOpen(false);
      setCellMenu(null);
      overlayOpenRef.current = !!confirmBox;
      if (!confirmBox) focusScan();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen, cellMenu, confirmBox]);

  // ── 서버 응답 적용 ──
  const noteEvent = (n) => {
    if (typeof n !== 'number' || !Number.isFinite(n)) return;
    lastEventRef.current = lastEventRef.current === null ? n : Math.max(lastEventRef.current, n);
  };
  const mutated = () => { mutationSeqRef.current += 1; };

  const applyCells = (d) => {
    if (!d) return;
    if (Array.isArray(d.ready_cells)) setReadyCells(d.ready_cells);
    if (d.progress) setProgress(d.progress);
    if (Array.isArray(d.board)) setBoard(d.board);
  };

  // 전체 보기({job,progress,board,ready_cells,last_event_id}) — 내가 가진 것보다 낡은 응답은 버린다.
  const applyView = (d, poll) => {
    if (!d || !mountedRef.current) return false;
    const ev = typeof d.last_event_id === 'number' ? d.last_event_id : null;
    if (ev !== null && lastEventRef.current !== null && ev < lastEventRef.current) return false;
    if (poll && poll.sentSeq !== mutationSeqRef.current) return false;
    noteEvent(ev);
    if (d.job) setJob(d.job);
    applyCells(d);
    return true;
  };

  const load = () => pdasAPI.get(id).then(
    (r) => {
      if (!mountedRef.current) return;
      setLoadError('');
      applyView(r.data);
    },
    async (err) => {
      const message = await errorText(err, '작업을 불러오지 못했습니다.');
      if (mountedRef.current) setLoadError(message);
    },
  );

  const poll = () => {
    if (pollingRef.current || !mountedRef.current) return;
    pollingRef.current = true;
    const sentSeq = mutationSeqRef.current;
    pdasAPI.get(id)
      .then((r) => { applyView(r.data, { sentSeq }); }, () => { /* 다음 갱신에서 다시 */ })
      .finally(() => { pollingRef.current = false; });
  };

  // 작업이 바뀌면 처음부터.
  useEffect(() => {
    queueRef.current = [];
    sendingRef.current = false;
    clearTimeout(retryTimerRef.current);
    retryTimerRef.current = null;
    lastEventRef.current = null;
    lastScanRef.current = null;
    setJob(null);
    setResult(null);
    setRecent([]);
    setLastPut(null);
    setQueueCount(0);
    load();
  }, [id]);

  const jobStatus = job ? job.status : null;

  // 작업 중에만 5초마다 다시 받는다(입력칸 초점·입력 중 글자는 건드리지 않는다).
  useEffect(() => {
    if (jobStatus !== 'active') return undefined;
    const timer = setInterval(() => poll(), POLL_MS);
    return () => clearInterval(timer);
  }, [jobStatus, id]);

  // 화면에 들어오면 스캔 칸에 초점.
  useEffect(() => {
    if (jobStatus === 'active' && scanRef.current) scanRef.current.focus();
  }, [jobStatus]);

  // 셀 수 칸은 서버 값을 따른다(준비 단계).
  useEffect(() => {
    if (job) setCellInput(String(job.cell_count ?? ''));
  }, [job ? job.cell_count : null]);

  // ── 명단 읽기(준비: 앞 50행 · 마감: 상태 거르기 + 쪽) ──
  const loadPlan = (params, setter) => {
    const seq = planSeqRef.current + 1;
    planSeqRef.current = seq;
    pdasAPI.plan(id, params).then(
      (r) => {
        if (!mountedRef.current || planSeqRef.current !== seq) return;
        setter({ items: Array.isArray(r.data?.items) ? r.data.items : [], total: Number(r.data?.total) || 0 });
      },
      () => { if (mountedRef.current && planSeqRef.current === seq) setter(null); },
    );
  };

  useEffect(() => {
    if (jobStatus === 'draft') loadPlan({ limit: PLAN_PREVIEW, offset: 0 }, setPlanPreview);
  }, [jobStatus, id]);

  useEffect(() => {
    if (jobStatus !== 'closed') return;
    const params = { limit: PLAN_PAGE, offset: (planPage - 1) * PLAN_PAGE };
    if (planStatus) params.status = planStatus;
    loadPlan(params, setPlanList);
  }, [jobStatus, id, planStatus, planPage]);

  // ── 확인창 ──
  const focusScan = () => { setTimeout(() => { if (mountedRef.current && scanRef.current) scanRef.current.focus(); }, 0); };
  const openConfirm = (cfg) => { overlayOpenRef.current = true; setConfirmBox(cfg); };
  const closeConfirm = () => { setConfirmBox(null); focusScan(); };
  const confirmAndRun = () => {
    const run = confirmBox && confirmBox.onConfirm;
    closeConfirm();
    if (run) run();
  };

  // ── 소리·진동 ──
  const feedback = (kind) => {
    vibrate(VIBRATE[kind]);
    if (soundRef.current) beep(kind);
  };
  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    try { localStorage.setItem(SOUND_KEY, next ? '1' : '0'); } catch { /* 기억 못 해도 동작 */ }
  };

  const pushRecent = (entry) => {
    recentSeqRef.current += 1;
    const item = { key: `r${recentSeqRef.current}`, ...entry };
    setRecent((prev) => [item, ...prev].slice(0, 3));
  };

  // ── 엑셀 받기 ──
  const downloadTemplate = async () => {
    try {
      const r = await pdasAPI.template();
      downloadBlob(r.data, '농심DAS_명단양식.xlsx');
    } catch (err) {
      setDraftError(await errorText(err, '빈 양식을 받지 못했습니다.'));
    }
  };

  const downloadResult = async () => {
    setMenuOpen(false);
    if (downloadingRef.current) return;
    downloadingRef.current = true;
    setDownloading(true);
    try {
      const r = await pdasAPI.result(id);
      downloadBlob(r.data, resultFileName(job));
    } catch (err) {
      const message = await errorText(err, '결과 엑셀을 받지 못했습니다.');
      if (mountedRef.current) setNotice(message);
    } finally {
      downloadingRef.current = false;
      if (mountedRef.current) setDownloading(false);
      focusScan();
    }
  };

  // ── 준비 ──
  const saveCells = async () => {
    if (!validCells(cellInput) || savingCells) return;
    setSavingCells(true);
    setDraftError('');
    try {
      const r = await pdasAPI.update(id, { cell_count: Number(cellInput) });
      mutated();
      applyView(r.data);
    } catch (err) {
      setDraftError(await errorText(err, '셀 수를 저장하지 못했습니다.'));
    } finally {
      if (mountedRef.current) setSavingCells(false);
    }
  };

  const onPlanFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    try { e.target.value = ''; } catch { /* 같은 파일을 다시 고를 수 있게 비운다 */ }
    if (!file) return;
    setUploading(true);
    setUploadInfo(null);
    setPlanErrors(null);
    setDraftError('');
    try {
      const r = await pdasAPI.uploadPlan(id, file);
      if (!mountedRef.current) return;
      mutated();
      setUploadInfo(r.data || {});
      load();
      loadPlan({ limit: PLAN_PREVIEW, offset: 0 }, setPlanPreview);
    } catch (err) {
      const detail = errorDetail(err);
      if (detail && Array.isArray(detail.errors)) {
        setPlanErrors({ message: errorTextSync(err, '명단에 오류가 있습니다.'), errors: detail.errors });
      } else {
        setDraftError(await errorText(err, '명단을 올리지 못했습니다.'));
      }
    } finally {
      if (mountedRef.current) setUploading(false);
    }
  };

  const doStart = async () => {
    setStarting(true);
    setDraftError('');
    try {
      const r = await pdasAPI.start(id);
      mutated();
      applyView(r.data);
    } catch (err) {
      setDraftError(await errorText(err, '작업을 시작하지 못했습니다.'));
    } finally {
      if (mountedRef.current) setStarting(false);
    }
  };

  const bundleCount = progress ? Number(progress.bundles_total) || 0 : 0;
  const askStart = () => {
    if (!job || bundleCount === 0) return;
    openConfirm({
      message: `셀 ${job.cell_count}개 · 묶음 ${bundleCount}개로 시작합니다. 시작하면 명단은 바꿀 수 없습니다.`,
      confirmLabel: '시작',
      onConfirm: doStart,
    });
  };

  // ── 작업 중: 스캔 줄 ──
  const onScanOk = (d) => {
    mutated();
    noteEvent(typeof d.last_event_id === 'number' ? d.last_event_id : d.put_id);
    if (Array.isArray(d.ready_cells)) setReadyCells(d.ready_cells);
    if (d.progress) setProgress(d.progress);
    if (d.cell_no) {
      // 스캔 응답엔 셀 판이 없다 — 넣은 셀만 바로 고친다(나머지는 다음 갱신).
      setBoard((prev) => prev.map((c) => (c.cell_no === d.cell_no ? {
        ...c,
        state: d.completed ? 'complete' : 'filling',
        bundle_id: d.bundle_id ?? c.bundle_id,
        bundle_no: d.bundle_no ?? c.bundle_no,
        barcode: d.barcode ?? c.barcode,
        qty: d.qty ?? c.qty,
        plan_qty: d.plan_qty ?? c.plan_qty,
      } : c)));
    }
    const completed = d.completed === true;
    setResult({ kind: completed ? 'complete' : 'put', cell: d.cell_no, bundleNo: d.bundle_no, qty: d.qty, planQty: d.plan_qty });
    const hasPut = typeof d.put_id === 'number';
    pushRecent({ text: putLine(d), putId: hasPut ? d.put_id : null, undone: false });
    if (hasPut) setLastPut({ putId: d.put_id, undone: false });
    feedback(completed ? 'complete' : 'put');
  };

  const onScanReject = (err) => {
    const detail = errorDetail(err);
    const code = detail ? detail.code : null;
    const text = errorTextSync(err, '스캔을 처리하지 못했습니다.');
    setResult({ kind: 'reject', text });
    pushRecent({ text: `거절 · ${REJECT_SHORT[code] || text}` });
    feedback('reject');
    if (code === 'job_not_active') load();
  };

  const pump = () => {
    if (!mountedRef.current || sendingRef.current || retryTimerRef.current) return;
    const head = queueRef.current[0];
    if (!head) return;
    sendingRef.current = true;
    pdasAPI.scan(id, head.barcode, head.scanId).then(
      (r) => {
        sendingRef.current = false;
        if (queueRef.current[0] === head) queueRef.current.shift();
        if (!mountedRef.current) return;
        setQueueCount(queueRef.current.length);
        onScanOk((r && r.data) || {});
        pump();
      },
      (err) => {
        sendingRef.current = false;
        if (!mountedRef.current) return;
        if (isNetworkError(err)) {
          // 줄을 멈추고 같은 scan_id 로 다시 보낸다(서버가 한 번만 넣는다).
          setResult({ kind: 'offline' });
          retryTimerRef.current = setTimeout(() => { retryTimerRef.current = null; pump(); }, RETRY_MS);
          return;
        }
        if (queueRef.current[0] === head) queueRef.current.shift();
        setQueueCount(queueRef.current.length);
        onScanReject(err);
        pump();
      },
    );
  };

  const enqueueScan = (raw) => {
    const barcode = String(raw || '').trim();
    if (!barcode) return;
    const now = Date.now();
    const last = lastScanRef.current;
    if (last && last.barcode === barcode && now - last.at < DUP_WINDOW_MS) {
      feedback('warn');   // 스캐너 이중 읽힘 — 버린다
      return;
    }
    lastScanRef.current = { barcode, at: now };
    queueRef.current.push({ barcode, scanId: newScanId() });
    setQueueCount(queueRef.current.length);
    pump();
  };

  const onScanKeyDown = (e) => {
    if (e.key !== 'Enter' && e.key !== 'Tab') return;
    e.preventDefault();
    const value = e.currentTarget.value;
    setScanText('');
    enqueueScan(value);
  };

  // 다른 단추를 누른 뒤에도 스캔 칸으로 — 겹쳐 뜬 것이 열려 있으면 그대로 둔다.
  const onScanBlur = () => {
    setTimeout(() => {
      if (!mountedRef.current || overlayOpenRef.current) return;
      const el = scanRef.current;
      if (el && document.activeElement !== el) el.focus();
    }, 0);
  };

  // ── 작업 중: 빼냄 · 빼냄 취소 · 강제 마감 · 되돌리기 · 마감 ──
  const setRemovingCell = (cell, on) => {
    if (on) removingRef.current.add(cell);
    else removingRef.current.delete(cell);
    setRemoving(new Set(removingRef.current));
  };

  const removeCell = (cell, bundleId) => {
    if (removingRef.current.has(cell)) return;
    setRemovingCell(cell, true);
    pdasAPI.remove(id, cell, bundleId).then(
      (r) => {
        if (!mountedRef.current) return;
        mutated();
        applyCells(r.data);
        setResult({ kind: 'info', text: `${cell}번 셀 비움` });
        clearTimeout(unremoveTimerRef.current);
        setUndoRemove({ cell, bundleId });
        unremoveTimerRef.current = setTimeout(() => { if (mountedRef.current) setUndoRemove(null); }, UNREMOVE_MS);
      },
      (err) => {
        if (!mountedRef.current) return;
        setResult({ kind: 'reject', text: errorTextSync(err, '빼냄을 처리하지 못했습니다.') });
        feedback('reject');
        // 409(stale_cell 등) = 화면이 서버와 어긋났다 → 다시 받아 그린다.
        if (!isNetworkError(err)) load();
      },
    ).finally(() => { if (mountedRef.current) setRemovingCell(cell, false); });
  };

  const cancelRemove = () => {
    const u = undoRemove;
    if (!u || unremovingRef.current) return;
    unremovingRef.current = true;
    clearTimeout(unremoveTimerRef.current);
    setUndoRemove(null);
    pdasAPI.unremove(id, u.cell, u.bundleId).then(
      (r) => {
        if (!mountedRef.current) return;
        mutated();
        applyCells(r.data);
        setResult({ kind: 'info', text: `${u.cell}번 셀 빼냄 취소` });
      },
      (err) => {
        if (!mountedRef.current) return;
        setResult({ kind: 'reject', text: errorTextSync(err, '빼냄 취소를 처리하지 못했습니다.') });
        feedback('reject');
        if (!isNetworkError(err)) load();
      },
    ).finally(() => { unremovingRef.current = false; });
  };

  const doForce = (cell, bundleId) => {
    pdasAPI.force(id, cell, bundleId).then(
      (r) => {
        if (!mountedRef.current) return;
        mutated();
        applyCells(r.data);
        setResult({ kind: 'forced', text: `${cell}번 셀 강제 마감 — 빼세요` });
        feedback('complete');
      },
      (err) => {
        if (!mountedRef.current) return;
        setResult({ kind: 'reject', text: errorTextSync(err, '강제 마감하지 못했습니다.') });
        feedback('reject');
        if (!isNetworkError(err)) load();
      },
    );
  };

  const askForce = (c) => {
    setCellMenu(null);
    openConfirm({
      message: `${c.cell_no}번 셀 — 계획 ${c.plan_qty}개 중 ${c.qty}개로 마감합니다`,
      confirmLabel: '강제 마감',
      onConfirm: () => doForce(c.cell_no, c.bundle_id),
    });
  };

  const undoPut = (putId) => {
    if (undoingRef.current || typeof putId !== 'number') return;
    undoingRef.current = true;
    setUndoing(true);
    pdasAPI.undo(id, putId).then(
      (r) => {
        if (!mountedRef.current) return;
        mutated();
        const d = r.data || {};
        applyCells(d);
        const where = d.cell_no ? `${d.cell_no}번 셀 ` : '';
        setResult({ kind: 'info', text: `취소: ${where}${d.qty}/${d.plan_qty}` });
        setRecent((prev) => prev.map((x) => (x.putId === putId ? { ...x, undone: true } : x)));
        setLastPut((prev) => (prev && prev.putId === putId ? { ...prev, undone: true } : prev));
      },
      (err) => {
        if (!mountedRef.current) return;
        setResult({ kind: 'reject', text: errorTextSync(err, '넣기를 취소하지 못했습니다.') });
        feedback('reject');
        const code = errorDetail(err)?.code;
        // 이미 되돌렸거나 남의 넣기 — 다시 고르지 못하게 표시한다.
        if (code === 'nothing_to_undo') {
          setRecent((prev) => prev.map((x) => (x.putId === putId ? { ...x, undone: true } : x)));
          setLastPut((prev) => (prev && prev.putId === putId ? { ...prev, undone: true } : prev));
        }
      },
    ).finally(() => {
      undoingRef.current = false;
      if (mountedRef.current) setUndoing(false);
    });
  };

  const closeJob = (confirmed) => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    setNotice('');
    pdasAPI.close(id, confirmed).then(
      (r) => {
        if (!mountedRef.current) return;
        mutated();
        applyView(r.data);
      },
      (err) => {
        if (!mountedRef.current) return;
        const detail = errorDetail(err);
        if (detail && detail.code === 'pending_left') {
          const left = typeof detail.pending === 'number' ? detail.pending : Number(progress && progress.pending) || 0;
          openConfirm({
            message: `아직 시작 안 한 묶음 ${left}개가 남았습니다. 그래도 마감할까요?`,
            confirmLabel: '마감',
            onConfirm: () => closeJob(true),
          });
          return;
        }
        let text = errorTextSync(err, '작업을 마감하지 못했습니다.');
        if (detail && detail.code === 'cells_in_use' && Array.isArray(detail.cells) && detail.cells.length) {
          text = `${text} (셀 ${detail.cells.join(', ')})`;
        }
        setNotice(text);
        if (detail && detail.code === 'job_closed') load();
      },
    ).finally(() => {
      closingRef.current = false;
      if (mountedRef.current) setClosing(false);
    });
  };

  // ── 그리기 ──
  const p = progress || {};
  const total = Number(p.bundles_total) || 0;
  const removed = Number(p.removed) || 0;
  const progressText = `빼냄 ${removed} / 전체 ${total} 묶음 · 남은 묶음 ${total - removed} · 단위 ${Number(p.units_done) || 0}/${Number(p.units_plan) || 0}`;
  const progressBar = (
    <div style={{ height: '8px', background: COLORS.borderSoft, borderRadius: '4px', overflow: 'hidden', marginTop: '6px' }}>
      <div style={{ width: `${total ? Math.round((removed / total) * 100) : 0}%`, height: '100%', background: COLORS.success }} />
    </div>
  );

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
      <button type="button" onClick={() => navigate('/pdas')} style={btn('secondary', 'sm')}>‹ 목록</button>
      <h2 style={{ margin: 0, fontSize: '18px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job ? job.title : '농심DAS'}</h2>
      {job && <span style={badge(job.status === 'active' ? 'success' : job.status === 'closed' ? 'default' : 'warn')}>{labelOf(JOB_STATUS_LABELS, job.status)}</span>}
      <span style={{ marginLeft: 'auto', display: 'flex', gap: '6px', alignItems: 'center' }}>
        {jobStatus === 'active' && (
          <button type="button" aria-pressed={soundOn} onClick={toggleSound} style={btn('secondary', 'sm')}>{soundOn ? '소리 켬' : '소리 끔'}</button>
        )}
        {job && jobStatus !== 'closed' && (
          <span data-pdas-menu="" style={{ position: 'relative' }}>
            <button type="button" aria-label="더 보기" aria-haspopup="menu" aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)} style={btn('secondary', 'sm')}>…</button>
            {menuOpen && (
              <div role="menu" onKeyDown={(e) => { if (e.key === 'Escape') { setMenuOpen(false); focusScan(); } }}
                style={{ position: 'absolute', right: 0, top: '110%', ...card, padding: '6px', zIndex: 20, minWidth: '160px' }}>
                <button type="button" role="menuitem" onClick={downloadResult} disabled={downloading}
                  style={{ ...btn('secondary', 'sm'), width: '100%', textAlign: 'left' }}>결과 엑셀 받기</button>
              </div>
            )}
          </span>
        )}
      </span>
    </div>
  );

  const renderDraft = () => (
    <>
      <section aria-label="셀 수" style={{ ...card, marginBottom: '10px' }}>
        <div style={{ fontWeight: 700, marginBottom: '8px' }}>① 셀 수</div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input aria-label="셀 수" type="number" min={1} max={CELLS_MAX} inputMode="numeric" value={cellInput}
            onChange={(e) => setCellInput(e.target.value)} style={{ ...input, width: '120px' }} />
          <button type="button" onClick={saveCells}
            disabled={savingCells || !validCells(cellInput) || Number(cellInput) === job.cell_count}
            style={btn('primary', 'sm')}>{savingCells ? '저장 중…' : '저장'}</button>
        </div>
      </section>

      <section aria-label="명단" style={{ ...card, marginBottom: '10px' }}>
        <div style={{ fontWeight: 700, marginBottom: '8px' }}>② 명단(묶음번호·바코드·수량)</div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button type="button" onClick={downloadTemplate} style={btn('secondary', 'sm')}>빈 양식 받기</button>
          <button type="button" onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading} style={btn('primary', 'sm')}>
            {uploading ? '올리는 중…' : '명단 올리기'}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.csv" data-testid="pdas-plan-file" onChange={onPlanFile} style={{ display: 'none' }} />
        </div>
        {job.plan_filename && <div style={{ fontSize: '12px', color: COLORS.textSub, marginTop: '8px' }}>{`올린 파일: ${job.plan_filename}`}</div>}
        {uploadInfo && (
          <div data-testid="pdas-plan-summary" style={{ marginTop: '10px', fontWeight: 700, color: COLORS.successText }}>
            {`묶음 ${Number(uploadInfo.rows) || 0}개 · 바코드 ${Number(uploadInfo.barcodes) || 0}종 · 단위 ${Number(uploadInfo.units_plan) || 0}개`}
          </div>
        )}
        {uploadInfo && Array.isArray(uploadInfo.warnings) && uploadInfo.warnings.length > 0 && (
          <div data-testid="pdas-plan-warnings" style={{ marginTop: '8px', padding: '8px 10px', borderRadius: '8px', background: COLORS.warnSoft, color: COLORS.warn, fontSize: '12px' }}>
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              {uploadInfo.warnings.map((w, i) => <li key={`${i}-${w}`}>{w}</li>)}
            </ul>
          </div>
        )}
        {planErrors && (
          <div role="alert" style={{ marginTop: '10px', padding: '8px 10px', borderRadius: '8px', background: COLORS.dangerSoft, color: COLORS.dangerText, fontSize: '13px' }}>
            <div style={{ fontWeight: 700 }}>{planErrors.message}</div>
            <table aria-label="명단 오류" style={{ borderCollapse: 'collapse', marginTop: '6px', background: COLORS.surface, color: COLORS.text }}>
              <thead><tr><th style={th}>행</th><th style={th}>문제</th></tr></thead>
              <tbody>
                {planErrors.errors.map((e, i) => (
                  <tr key={`${e.row}-${i}`} data-testid="pdas-plan-error-row">
                    <td style={td}>{e.row}</td>
                    <td style={{ ...td, whiteSpace: 'normal' }}>{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: '6px' }}>기존 명단은 바뀌지 않았습니다.</div>
          </div>
        )}
        {planPreview && planPreview.items.length > 0 && (
          <div style={{ marginTop: '10px', overflowX: 'auto' }}>
            <table aria-label="명단 미리보기" style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr><th style={th}>묶음번호</th><th style={th}>바코드</th><th style={th}>수량</th></tr></thead>
              <tbody>
                {planPreview.items.map((b) => (
                  <tr key={b.bundle_id}><td style={td}>{b.bundle_no}</td><td style={td}>{b.barcode}</td><td style={td}>{b.plan_qty}</td></tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: '12px', color: COLORS.textSub, marginTop: '4px' }}>{`전체 ${planPreview.total}행 중 앞 ${planPreview.items.length}행`}</div>
          </div>
        )}
      </section>

      <section aria-label="작업 시작" style={{ ...card, marginBottom: '10px' }}>
        <div style={{ fontWeight: 700, marginBottom: '8px' }}>③ 시작</div>
        <button type="button" onClick={askStart} disabled={bundleCount === 0 || starting}
          style={{ ...btn('primary'), opacity: bundleCount === 0 ? 0.5 : 1 }}>{starting ? '시작하는 중…' : '작업 시작'}</button>
        {bundleCount === 0 && <span style={{ marginLeft: '8px', fontSize: '12px', color: COLORS.textSub }}>명단을 먼저 올려 주세요.</span>}
      </section>
      {draftError && <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText }}>{draftError}</div>}
    </>
  );

  const renderResult = () => {
    const r = result;
    const bg = r ? RESULT_BG[r.kind] || RESULT_BG.info : '#e5e7eb';
    return (
      <div data-testid="pdas-result" data-kind={r ? r.kind : 'idle'} aria-live="polite"
        style={{ marginTop: '10px', borderRadius: '12px', padding: '14px 10px', minHeight: '120px', textAlign: 'center', background: bg, color: r ? '#fff' : COLORS.textSub, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px' }}>
        {!r && <div style={{ fontSize: '18px' }}>바코드를 스캔하세요</div>}
        {r && (r.kind === 'put' || r.kind === 'complete') && (
          <>
            <div style={{ fontSize: '72px', fontWeight: 900, lineHeight: 1.05 }}>{`${r.cell}번 셀`}</div>
            {r.kind === 'complete' && <div style={{ fontSize: '24px', fontWeight: 800 }}>{`${r.cell}번 셀 완성 — 빼세요`}</div>}
            <div style={{ fontSize: '18px' }}>{bundleLine(r)}</div>
          </>
        )}
        {r && r.kind === 'offline' && <div style={{ fontSize: '24px', fontWeight: 800 }}>연결 끊김 — 다시 보내는 중</div>}
        {r && (r.kind === 'reject' || r.kind === 'info' || r.kind === 'forced') && (
          <div style={{ fontSize: '26px', fontWeight: 800, lineHeight: 1.3 }}>{r.text}</div>
        )}
      </div>
    );
  };

  const renderActive = () => {
    const ready = [...readyCells].sort((a, b) => a.cell_no - b.cell_no);
    return (
      <>
        <section aria-label="스캔">
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input ref={scanRef} aria-label="바코드 스캔" data-testid="pdas-scan-input" value={scanText}
              onChange={(e) => setScanText(e.target.value)} onKeyDown={onScanKeyDown} onBlur={onScanBlur}
              inputMode="none" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
              placeholder="바코드 스캔" style={{ ...input, fontSize: '18px', padding: '12px' }} />
            {queueCount > QUEUE_SHOW && <span data-testid="pdas-queue" style={{ ...badge('warn'), fontSize: '13px' }}>{`대기 ${queueCount}건`}</span>}
          </div>
          {renderResult()}
          {undoRemove && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', fontSize: '14px' }}>
              <span>{`${undoRemove.cell}번 셀 비움`}</span>
              <button type="button" onClick={cancelRemove} style={btn('secondary', 'sm')}>취소</button>
            </div>
          )}
          <ol aria-label="최근 스캔" data-testid="pdas-recent" style={{ listStyle: 'none', padding: 0, margin: '6px 0 0', fontSize: '13px', color: COLORS.textSub }}>
            {recent.map((x) => (
              <li key={x.key} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '2px 0', textDecoration: x.undone ? 'line-through' : 'none' }}>
                {x.text}
                {x.putId !== null && x.putId !== undefined && !x.undone && (
                  <button type="button" aria-label={`${x.text} 넣기 취소`} onClick={() => undoPut(x.putId)} disabled={undoing}
                    style={{ ...btn('secondary', 'sm'), padding: '2px 6px', fontSize: '11px' }}>취소</button>
                )}
              </li>
            ))}
          </ol>
        </section>

        <section aria-label="빼세요" style={{ marginTop: '12px' }}>
          <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '6px' }}>빼세요</div>
          {ready.length === 0 && <div style={{ fontSize: '13px', color: COLORS.textMuted }}>뺄 셀이 없습니다.</div>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {ready.map((c) => (
              <button key={`${c.cell_no}-${c.bundle_id}`} type="button" disabled={removing.has(c.cell_no)}
                onClick={() => removeCell(c.cell_no, c.bundle_id)}
                style={{ padding: '14px 18px', fontSize: '22px', fontWeight: 800, border: 'none', borderRadius: '10px', background: '#ea580c', color: '#fff', cursor: 'pointer', opacity: removing.has(c.cell_no) ? 0.5 : 1 }}>
                {c.forced ? `${c.cell_no}번 빼냄 (부족)` : `${c.cell_no}번 빼냄`}
              </button>
            ))}
          </div>
        </section>

        <section aria-label="진행 현황" style={{ marginTop: '12px' }}>
          <div data-testid="pdas-progress" style={{ fontSize: '13px' }}>{progressText}</div>
          {progressBar}
        </section>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
          <button type="button" onClick={() => lastPut && undoPut(lastPut.putId)}
            disabled={!lastPut || lastPut.undone || undoing} style={btn('secondary')}>마지막 넣기 취소</button>
          <button type="button" onClick={() => closeJob(false)} disabled={closing} style={{ ...btn('danger'), marginLeft: 'auto' }}>작업 마감</button>
        </div>
        {notice && <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, marginTop: '10px' }}>{notice}</div>}

        <section aria-label="셀 판" style={{ marginTop: '14px' }}>
          <button type="button" aria-expanded={boardOpen} onClick={() => setBoardOpen((o) => !o)} style={btn('secondary', 'sm')}>
            {boardOpen ? '셀 판 접기' : '셀 판 펼치기'}
          </button>
          {boardOpen && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(76px, 1fr))', gap: '6px', marginTop: '8px' }}>
              {board.map((c) => {
                const look = CELL_STYLE[c.state] || CELL_STYLE.empty;
                return (
                  <div key={c.cell_no} data-testid="pdas-cell" data-state={c.state}
                    style={{ position: 'relative', border: '2px solid', borderRadius: '8px', padding: '6px', minHeight: '64px', ...look }}>
                    <div style={{ fontWeight: 800, fontSize: '18px' }}>{c.cell_no}</div>
                    {c.bundle_no && <div style={{ fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.bundle_no}</div>}
                    {c.plan_qty !== null && c.plan_qty !== undefined && <div style={{ fontSize: '12px' }}>{`${c.qty}/${c.plan_qty}`}</div>}
                    {c.state === 'filling' && (
                      <button type="button" data-pdas-menu="" aria-label={`${c.cell_no}번 셀 메뉴`} aria-haspopup="menu" aria-expanded={cellMenu === c.cell_no}
                        onClick={() => setCellMenu(cellMenu === c.cell_no ? null : c.cell_no)}
                        style={{ position: 'absolute', top: '4px', right: '4px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '16px', padding: '0 4px' }}>…</button>
                    )}
                    {cellMenu === c.cell_no && (
                      <div role="menu" data-pdas-menu="" onKeyDown={(e) => { if (e.key === 'Escape') { setCellMenu(null); focusScan(); } }}
                        style={{ position: 'absolute', top: '26px', right: '4px', ...card, padding: '4px', zIndex: 10 }}>
                        <button type="button" role="menuitem" onClick={() => askForce(c)} style={btn('danger', 'sm')}>강제 마감</button>
                      </div>
                    )}
                    {(c.state === 'complete' || c.state === 'forced') && (
                      <button type="button" aria-label={`${c.cell_no}번 셀 빼냄`} disabled={removing.has(c.cell_no)}
                        onClick={() => removeCell(c.cell_no, c.bundle_id)}
                        style={{ ...btn('secondary', 'sm'), marginTop: '4px', padding: '3px 6px' }}>빼냄</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </>
    );
  };

  const renderClosed = () => {
    const list = planList ? planList.items : [];
    const pages = planList ? Math.max(1, Math.ceil(planList.total / PLAN_PAGE)) : 1;
    return (
      <>
        <section aria-label="마감 요약" style={{ ...card, marginBottom: '10px' }}>
          <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 12px', margin: '0 0 10px', fontSize: '13px' }}>
            <dt style={{ color: COLORS.textSub }}>작업 이름</dt><dd style={{ margin: 0 }}>{job.title}</dd>
            <dt style={{ color: COLORS.textSub }}>셀 수</dt><dd style={{ margin: 0 }}>{job.cell_count}</dd>
            <dt style={{ color: COLORS.textSub }}>시작</dt><dd style={{ margin: 0 }}>{fmtKstDateTime(job.started_at) || '—'}</dd>
            <dt style={{ color: COLORS.textSub }}>마감</dt><dd style={{ margin: 0 }}>{fmtKstDateTime(job.closed_at) || '—'}</dd>
            <dt style={{ color: COLORS.textSub }}>진행</dt><dd data-testid="pdas-progress" style={{ margin: 0 }}>{progressText}</dd>
            <dt style={{ color: COLORS.textSub }}>강제 마감 묶음</dt><dd style={{ margin: 0 }}>{`${Number(p.forced_total) || 0}개`}</dd>
          </dl>
          <button type="button" onClick={downloadResult} disabled={downloading} style={btn('primary')}>
            {downloading ? '받는 중…' : '결과 엑셀 받기'}
          </button>
          {notice && <div role="alert" style={{ marginTop: '8px', color: COLORS.dangerText, fontSize: '13px' }}>{notice}</div>}
        </section>

        <section aria-label="명단" style={{ ...card }}>
          <label style={{ fontSize: '12px' }}>상태 <select aria-label="상태 거르기" value={planStatus}
            onChange={(e) => { setPlanStatus(e.target.value); setPlanPage(1); }} style={{ ...input, width: '140px', marginLeft: '4px' }}>
            <option value="">전체</option>
            {Object.entries(BUNDLE_STATUS_LABELS).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select></label>
          <div style={{ overflowX: 'auto', marginTop: '8px' }}>
            <table aria-label="명단 결과" style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr>{['묶음번호', '바코드', '계획', '넣은 수', '상태', '셀'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {list.map((b) => (
                  <tr key={b.bundle_id}>
                    <td style={td}>{b.bundle_no}</td>
                    <td style={td}>{b.barcode}</td>
                    <td style={td}>{b.plan_qty}</td>
                    <td style={td}>{b.qty}</td>
                    <td style={td}>{labelOf(BUNDLE_STATUS_LABELS, b.status)}{b.forced && b.status !== 'forced' ? ' (강제 마감)' : ''}</td>
                    <td style={td}>{b.cell_no ?? '—'}</td>
                  </tr>
                ))}
                {planList && list.length === 0 && (
                  <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: COLORS.textMuted }}>해당하는 묶음이 없습니다.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '8px', fontSize: '13px' }}>
              <button type="button" onClick={() => setPlanPage((n) => Math.max(1, n - 1))} disabled={planPage <= 1} style={btn('secondary', 'sm')}>‹ 이전</button>
              <span>{`${planPage} / ${pages}`}</span>
              <button type="button" onClick={() => setPlanPage((n) => Math.min(pages, n + 1))} disabled={planPage >= pages} style={btn('secondary', 'sm')}>다음 ›</button>
            </div>
          )}
        </section>
      </>
    );
  };

  return (
    <AppShell app="pdas">
      <main style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto', background: COLORS.bg }}>
        <div style={{ padding: '12px', maxWidth: '960px' }}>
          {header}
          {!job && loadError && (
            <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText }}>
              {loadError}{' '}
              <button type="button" onClick={load} style={btn('secondary', 'sm')}>다시 시도</button>
            </div>
          )}
          {!job && !loadError && <div role="status" style={{ color: COLORS.textMuted, fontSize: '13px' }}>불러오는 중…</div>}
          {jobStatus === 'draft' && renderDraft()}
          {jobStatus === 'active' && renderActive()}
          {jobStatus === 'closed' && renderClosed()}
        </div>
      </main>
      {confirmBox && (
        <GuardedConfirm message={confirmBox.message} confirmLabel={confirmBox.confirmLabel}
          onConfirm={confirmAndRun} onCancel={closeConfirm} />
      )}
    </AppShell>
  );
}
